export class SellingPlansWidget extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.enablePerformanceObserver();
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.bindWidgetEvents();
    document.addEventListener('click', this.onDocumentClick);
    this.updatePrice();
    this.toggleAfterpayVisibility();
    this.toggleSubscriptionTermsVisibility();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onWidgetChange);
    this.removeEventListener('click', this.onWidgetClick);
    this.removeEventListener('keydown', this.onWidgetKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get rootElement() {
    return this.closest('product-info');
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    const forms = this.addToCartForms;
    return (
      forms[1]?.querySelector(`input[name="id"]`) ||
      forms[1]?.querySelector(`select[name="id"]`) ||
      forms[0]?.querySelector(`input[name="id"]`) ||
      forms[0]?.querySelector(`select[name="id"]`)
    );
  }

  get priceElement() {
    return this.rootElement.querySelector('.price');
  }

  get comparedAtPrice() {
    return this.rootElement.querySelector('.price__sale');
  }

  get visibleSellingPlanForm() {
    return this.querySelector(`div[data-variant-id^="${this.variantIdInput?.value}"]`);
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption?.getAttributeNames().includes('disabled');
  }

  get sellingPlanInput() {
    return this.rootElement.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.rootElement.querySelectorAll('[action*="/cart/add"]');
  }

  get discountPercentageElement() {
    return this.priceElement?.querySelector('.price__discount-percentage');
  }

  get discountPercentageText() {
    return this.discountPercentageElement?.querySelector('.price__discount-percentage-text');
  }

  get isSubscribeSelected() {
    return this.selectedPurchaseOption?.dataset.radioType === 'selling_plan';
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    if (!this.sellingPlanInput) return;

    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove('selling_plan_theme_integration--hidden');
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add('selling_plan_theme_integration--hidden');
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.rootElement.querySelector(`div[data-variant-id="${this.variantIdInput.value}"]`);
    const sellingPlanFormsForUnselectedVariants = this.rootElement.querySelectorAll(
      `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`,
    );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.updatePrice();
    this.toggleAfterpayVisibility();
    this.toggleSubscriptionTermsVisibility();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput?.tagName === 'INPUT') {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  bindWidgetEvents() {
    this.addEventListener('change', this.onWidgetChange);
    this.addEventListener('click', this.onWidgetClick);
    this.addEventListener('keydown', this.onWidgetKeydown);
  }

  onWidgetChange = (event) => {
    const target = event.target;

    if (target.matches('[data-selling-plan-select]')) {
      this.handleFrequencyChange(target);
      return;
    }

    if (target.matches('input[type="radio"]')) {
      this.handleRadioButtonChange(target);
    }
  };

  onWidgetClick = (event) => {
    const trigger = event.target.closest('[data-frequency-trigger]');
    if (trigger) {
      this.toggleFrequencyAxis(trigger.closest('.product-sub__frequency-wrap'));
      return;
    }

    const option = event.target.closest('[data-frequency-option]');
    if (option) {
      this.selectFrequencyValue(option.closest('.product-sub__frequency-wrap'), option.dataset.value);
      return;
    }

    if (event.target.closest('select')) return;

    if (!event.target.closest('.product-sub__frequency-wrap')) {
      this.closeAllFrequencyAxes();
    }

    const card = event.target.closest('[data-sub-card]');
    if (!card) return;

    const radio = card.querySelector('input[type="radio"]');
    if (!radio || radio.checked || radio.disabled) return;

    // Announce it as a click on the radio itself would: onWidgetChange runs handleRadioButtonChange,
    // and listeners outside the widget (the data layer's product_option_selected) see the switch
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  toggleFrequencyAxis(axis) {
    if (!axis) return;

    const trigger = axis.querySelector('[data-frequency-trigger]');
    const panel = axis.querySelector('[data-frequency-panel]');
    const willOpen = panel.hidden;

    this.closeAllFrequencyAxes();
    if (!willOpen) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
  }

  closeAllFrequencyAxes() {
    for (const axis of this.querySelectorAll('.product-sub__frequency-wrap')) {
      axis.querySelector('[data-frequency-panel]').hidden = true;
      axis.querySelector('[data-frequency-trigger]').setAttribute('aria-expanded', 'false');
    }
  }

  selectFrequencyValue(axis, value) {
    if (!axis || value === undefined) return;

    const select = axis.querySelector('[data-selling-plan-select]');
    if (!select) return;

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    const selectedOption = axis.querySelector(`[data-frequency-option][data-value="${CSS.escape(value)}"]`);
    for (const option of axis.querySelectorAll('[data-frequency-option]')) {
      option.setAttribute('aria-selected', String(option === selectedOption));
    }

    const valueEl = axis.querySelector('[data-frequency-value]');
    if (valueEl) valueEl.textContent = selectedOption?.textContent.trim() ?? '';

    this.closeAllFrequencyAxes();
    axis.querySelector('[data-frequency-trigger]')?.focus();
  }

  onWidgetKeydown = (event) => {
    if (event.key === 'Escape') {
      const openTrigger = this.querySelector('[data-frequency-trigger][aria-expanded="true"]');
      if (openTrigger) {
        event.stopPropagation();
        this.closeAllFrequencyAxes();
        openTrigger.focus();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const axis = event.target.closest('.product-sub__frequency-wrap');
    if (!axis) return;

    const options = [...axis.querySelectorAll('[data-frequency-option]')];
    if (!options.length) return;

    event.preventDefault();
    const isOpen = axis.querySelector('[data-frequency-trigger]').getAttribute('aria-expanded') === 'true';
    if (!isOpen) {
      this.toggleFrequencyAxis(axis);
      options[0].focus();
      return;
    }

    const current = options.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
    options[Math.max(0, Math.min(options.length - 1, next))].focus();
  };

  onDocumentClick = (event) => {
    if (this.contains(event.target)) return;

    this.closeAllFrequencyAxes();
  };

  handleFrequencyChange(select) {
    this.syncSubscribeRadioFromSelect(select);

    const radio = this.visibleSellingPlanForm?.querySelector('[data-radio-type="selling_plan"]');
    if (!radio) return;

    // Picking a frequency while one-time is selected switches to subscribe; announce it like a click would
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    this.handleRadioButtonChange(radio);
  }

  syncSubscribeRadioFromSelect(select) {
    const selected = select.options[select.selectedIndex];
    const radio = this.visibleSellingPlanForm?.querySelector('[data-radio-type="selling_plan"]');
    if (!radio || !selected) return;

    radio.dataset.sellingPlanId = selected.dataset.sellingPlanId;
    radio.dataset.sellingPlanGroupId = selected.dataset.sellingPlanGroupId;
    radio.dataset.sellingPlanAdjustment = selected.dataset.sellingPlanAdjustment;
    radio.dataset.variantPrice = selected.dataset.variantPrice;
    radio.dataset.variantCompareAtPrice = selected.dataset.variantCompareAtPrice;
    radio.dataset.saveText = selected.dataset.saveText;
    radio.dataset.savePercentLabel = selected.dataset.savePercentLabel;
    radio.dataset.saveAmountLabel = selected.dataset.saveAmountLabel;
  }

  get regularPriceElement() {
    return this.rootElement.querySelector('.price__regular');
  }

  get salePriceElement() {
    return this.rootElement.querySelector('.price__sale');
  }

  get salePriceValue() {
    return this.salePriceElement?.querySelector('.price-item--sale');
  }

  get regularPriceValue() {
    return this.salePriceElement?.querySelector('.price-item--regular');
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(`${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`);
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption?.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption?.dataset.variantCompareAtPrice;
  }

  get selectedPurchaseOptionSaveText() {
    return this.selectedPurchaseOption?.dataset.saveText;
  }

  get selectedPurchaseOptionSavePercentLabel() {
    return this.selectedPurchaseOption?.dataset.savePercentLabel;
  }

  get selectedPurchaseOptionSaveAmountLabel() {
    return this.selectedPurchaseOption?.dataset.saveAmountLabel;
  }

  get subscribeDiscountLabel() {
    const showAmount = this.discountPercentageElement?.dataset.showAmount === 'true';
    if (showAmount && this.selectedPurchaseOptionSaveAmountLabel) {
      return this.selectedPurchaseOptionSaveAmountLabel;
    }

    return this.selectedPurchaseOptionSavePercentLabel;
  }

  get price() {
    return this.sellingPlanAllocationPrices?.price ?? null;
  }

  get atcPriceElement() {
    return this.rootElement?.querySelector('[data-atc-price]');
  }

  get atcCompareAtElement() {
    return this.rootElement?.querySelector('[data-atc-compare-at]');
  }

  updateAtcPrice() {
    const priceEl = this.atcPriceElement;
    if (!priceEl) {
      return;
    }

    const button = priceEl.closest('button');
    const price = this.selectedPurchaseOptionPrice;
    const compareEl = this.atcCompareAtElement;
    const compare = this.selectedPurchaseOptionComparedAtPrice;

    if (button?.disabled || !price) {
      priceEl.hidden = true;
      if (compareEl) compareEl.hidden = true;
      return;
    }

    priceEl.hidden = false;
    priceEl.textContent = price;

    if (compareEl) {
      const showCompare = Boolean(compare) && compare !== price;
      compareEl.textContent = showCompare ? compare : '';
      compareEl.hidden = !showCompare;
    }
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */
  updatePrice() {
    this.updateAtcPrice();

    if (!this.priceElement) return;

    const isSubscribe = this.isSubscribeSelected;
    this.priceElement.classList.toggle('price--subscription', isSubscribe);
    this.updateCardSelectedState();
    this.updateSubscribeCardPrices();

    if (!this.selectedPurchaseOptionComparedAtPrice || this.selectedPurchaseOptionComparedAtPrice === this.selectedPurchaseOptionPrice) {
      this.showRegularPrice();
      this.hideSalePrice();
      this.priceElement.classList.remove('price--on-sale');
      this.hideDiscountPill();
      return;
    }

    this.showSalePrice();
    this.hideRegularPrice();
    this.priceElement.classList.add('price--on-sale');

    if (isSubscribe) {
      this.showDiscountPill(this.subscribeDiscountLabel);
    } else {
      this.restoreDiscountPill();
    }
  }

  updateCardSelectedState() {
    const form = this.visibleSellingPlanForm;
    if (!form) return;

    const isSubscribe = this.isSubscribeSelected;
    form.querySelector('[data-sub-card="subscribe"]')?.classList.toggle('is-selected', isSubscribe);
    form.querySelector('[data-sub-card="otp"]')?.classList.toggle('is-selected', !isSubscribe);
  }

  updateSubscribeCardPrices() {
    const form = this.visibleSellingPlanForm;
    const option = this.selectedPurchaseOption;
    if (!form || option?.dataset.radioType !== 'selling_plan') return;

    const compareEl = form.querySelector('[data-sub-compare-price]');
    const saleEl = form.querySelector('[data-sub-sale-price]');
    const saveEl = form.querySelector('[data-sub-save-text]');

    if (compareEl) compareEl.textContent = option.dataset.variantCompareAtPrice;
    if (saleEl) saleEl.textContent = option.dataset.variantPrice;
    if (saveEl) saveEl.textContent = option.dataset.saveText || '';
  }

  hideSalePrice() {
    if (this.salePriceElement) this.salePriceElement.style.display = 'none';
  }

  hideRegularPrice() {
    if (this.regularPriceElement) this.regularPriceElement.style.display = 'none';
  }

  showRegularPrice() {
    if (this.regularPriceElement) this.regularPriceElement.style.display = 'block';
    if (this.salePriceElement) this.salePriceElement.style.display = 'none';
  }

  showSalePrice() {
    if (!this.salePriceElement) return;

    this.salePriceElement.style.display = this.priceElement?.classList.contains('price--subscription') ? 'flex' : '';

    if (this.regularPriceValue) {
      this.regularPriceValue.textContent = this.selectedPurchaseOptionComparedAtPrice;
    }

    if (this.salePriceValue) {
      this.salePriceValue.textContent = this.selectedPurchaseOptionPrice;
    }
  }

  cacheOriginalDiscountLabel() {
    const wrap = this.discountPercentageElement;
    const text = this.discountPercentageText;
    if (!wrap || !text || Object.prototype.hasOwnProperty.call(wrap.dataset, 'originalLabel')) return;

    wrap.dataset.originalLabel = text.textContent.trim();
  }

  showDiscountPill(label) {
    const wrap = this.discountPercentageElement;
    const text = this.discountPercentageText;
    if (!wrap || !text || !label) {
      this.hideDiscountPill();
      return;
    }

    this.cacheOriginalDiscountLabel();
    text.textContent = label;
    wrap.hidden = false;
  }

  restoreDiscountPill() {
    const wrap = this.discountPercentageElement;
    const text = this.discountPercentageText;
    if (!wrap || !text) {
      this.hideDiscountPill();
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(wrap.dataset, 'originalLabel')) {
      wrap.hidden = !text.textContent.trim();
      return;
    }

    const original = wrap.dataset.originalLabel;
    if (!original) {
      this.hideDiscountPill();
      return;
    }

    text.textContent = original;
    wrap.hidden = false;
  }

  hideDiscountPill() {
    const wrap = this.discountPercentageElement;
    if (!wrap) return;

    wrap.hidden = true;
  }

  get sellingPlanInputs() {
    return this.rootElement.querySelectorAll('.selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector('input[type="radio"]:checked');
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
    this.updatePrice();
    this.toggleAfterpayVisibility();
    this.toggleSubscriptionTermsVisibility();
  }

  toggleSubscriptionTermsVisibility() {
    const isOneTime = this.selectedPurchaseOption?.dataset.radioType === 'one_time_purchase';
    const notices = this.rootElement?.querySelectorAll('.subscription-terms-notice') ?? [];

    for (const el of notices) {
      el.hidden = isOneTime;
    }
  }

  toggleAfterpayVisibility() {
    const purchaseOption = this.selectedPurchaseOption;
    const isOneTime = !purchaseOption || purchaseOption.dataset.radioType === 'one_time_purchase';
    this.rootElement?.querySelectorAll('.product__afterpay').forEach((el) => {
      el.hidden = !isOneTime;
    });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {
          this.updatePrice();
        }
      });
    });

    performanceObserver.observe({ entryTypes: ['resource'] });
  }
}

if (!customElements.get('selling-plans-widget')) {
  customElements.define('selling-plans-widget', SellingPlansWidget);
}
